import { Box, Button, IconButton, Typography } from "@mui/material";
import React from "react";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { useLocation, useNavigate } from "react-router-dom";
import { categoryLabel, engineLabel } from "./walkIssueMeta";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";

// Read-only field block — label on top, value below.
const Field = ({ label, value, breakWord }) => (
  <Box>
    <Typography
      sx={{
        color: "#00000080",
        fontSize: "12px",
        fontWeight: "500",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: "#000",
        fontSize: "16px",
        fontWeight: "600",
        wordBreak: breakWord ? "break-word" : "normal",
        whiteSpace: "pre-wrap",
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </Typography>
  </Box>
);

const ReportedWalkIssueDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const report = location?.state?.report;

  const reporterName = report
    ? `${report.reporter_fname || ""} ${report.reporter_lname || ""}`.trim()
    : "";
  const fmt = (d) => (d ? moment(d).format("MMM D, YYYY h:mm A") : null);

  // Download the gzipped diagnostic slice for this report. The backend gunzips
  // server-side and returns plain text; we save it as a .txt file.
  const handleDownloadDiag = async () => {
    try {
      const res = await AxiosInstance.get(
        `${ApiList.GET_WALK_ISSUES}/${report.id}/diag`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `walk-${report.activity_id}-diag.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("diag download failed", e);
    }
  };

  const fmtKb = (bytes) =>
    bytes ? `${(bytes / 1024).toFixed(1)} KB` : null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        background: "#B9CC0310",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          width: "100%",
          p: 2,
          background: "#b9cc0310",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ color: "#000", fontSize: "28px" }} />
        </IconButton>
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "28px" },
            fontWeight: "300",
            color: "#000",
          }}
        >
          Reported Walk Issue Details
        </Typography>
      </Box>

      <Box sx={{ width: "100%", p: 2 }}>
        {!report ? (
          <Typography sx={{ color: "#000", fontSize: "16px" }}>
            No report selected. Go back to the list and open a report.
          </Typography>
        ) : (
          <Box
            sx={{
              background: "#fff",
              borderRadius: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Report */}
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#000",
                  mb: 1.5,
                }}
              >
                Report
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 3,
                }}
              >
                <Field label="Issue" value={categoryLabel(report.category)} />
                <Field label="Engine" value={engineLabel(report.tracking_engine)} />
                <Field label="Reported At" value={fmt(report.created_at)} />
              </Box>
            </Box>

            {/* Note */}
            <Box sx={{ borderTop: "1px solid #00000010", pt: 2 }}>
              <Field label="Note" value={report.note} breakWord />
            </Box>

            {/* Reporter */}
            <Box sx={{ borderTop: "1px solid #00000010", pt: 2 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#000",
                  mb: 1.5,
                }}
              >
                Reporter
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },
                  gap: 3,
                }}
              >
                <Field label="Name" value={reporterName} />
                <Field label="Email" value={report.reporter_email} breakWord />
              </Box>
            </Box>

            {/* Walk */}
            <Box sx={{ borderTop: "1px solid #00000010", pt: 2 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#000",
                  mb: 1.5,
                }}
              >
                Walk
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 3,
                }}
              >
                <Field label="Activity ID" value={report.activity_id} />
                <Field label="Title" value={report.walk_title} />
                <Field
                  label="Distance"
                  value={
                    report.walk_distance === null ||
                    report.walk_distance === undefined
                      ? null
                      : `${report.walk_distance} mi`
                  }
                />
                <Field label="Walk Date" value={fmt(report.walk_created_at)} />
              </Box>
            </Box>

            {/* Diagnostics */}
            <Box sx={{ borderTop: "1px solid #00000010", pt: 2 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#000",
                  mb: 1.5,
                }}
              >
                Diagnostics
              </Typography>
              {report.has_diag ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadDiag}
                    sx={{
                      background: "#b9cc03",
                      color: "#000",
                      textTransform: "none",
                      "&:hover": { background: "#a6b703" },
                    }}
                  >
                    Download diagnostic log
                  </Button>
                  <Typography sx={{ color: "#00000080", fontSize: "13px" }}>
                    {fmtKb(report.diag_bytes)}
                    {report.diag_truncated ? " · truncated" : ""}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ color: "#000", fontSize: "16px" }}>
                  No diagnostic log attached to this report.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportedWalkIssueDetails;
